import * as PIXI from 'pixi.js';
import { InputManager } from '@/systems';

type TutorialTab = 'mobile' | 'pc';

interface TutorialModalOptions {
  x?: number;
  y?: number;
}

export class TutorialModal {
  private container: PIXI.Container;
  private buttonContainer: PIXI.Container;
  private buttonSprite: PIXI.Sprite;
  private modalContainer: PIXI.Container;
  private overlay: PIXI.Graphics;
  private panel: PIXI.Graphics;
  private closeButton: PIXI.Container;
  private mobileTabButton: PIXI.Container;
  private pcTabButton: PIXI.Container;
  private tutorialTitle: PIXI.Text;
  private tutorialDescription: PIXI.Text;
  private tutorialImage: PIXI.Sprite;
  private tabButtons: Record<TutorialTab, PIXI.Container>;
  private tutorialTextures: Partial<Record<TutorialTab, PIXI.Texture>> = {};
  private screenWidth = 0;
  private screenHeight = 0;
  private activeTab: TutorialTab;
  private isOpen = false;
  private scale = 1;
  private x: number;
  private y: number;
  private readonly buttonSize = 60;
  private readonly buttonGap = 12;

  constructor(options: TutorialModalOptions = {}) {
    this.x = options.x ?? 10;
    this.y = options.y ?? 10;
    this.activeTab = InputManager.getInstance()?.isMobile() ? 'mobile' : 'pc';

    this.container = new PIXI.Container();
    this.container.visible = false;
    this.container.eventMode = 'static';
    this.container.sortableChildren = true;

    this.buttonContainer = new PIXI.Container();
    this.buttonContainer.position.set(this.x, this.y);
    this.buttonContainer.eventMode = 'static';
    this.buttonContainer.cursor = 'pointer';
    this.container.addChild(this.buttonContainer);

    const buttonBg = new PIXI.Graphics();
    buttonBg.beginFill(0xffffff, 0.01);
    buttonBg.drawRect(0, 0, this.buttonSize, this.buttonSize);
    buttonBg.endFill();
    this.buttonContainer.addChild(buttonBg);

    this.buttonSprite = new PIXI.Sprite(PIXI.Texture.WHITE);
    this.buttonSprite.width = this.buttonSize;
    this.buttonSprite.height = this.buttonSize;
    this.buttonSprite.tint = 0xd4af37;
    this.buttonContainer.addChild(this.buttonSprite);

    this.buttonContainer.on('pointerdown', (event: PIXI.FederatedPointerEvent) => {
      event.stopPropagation();
      this.toggle();
    });

    this.modalContainer = new PIXI.Container();
    this.modalContainer.visible = false;
    this.modalContainer.zIndex = 1200;
    this.modalContainer.sortableChildren = true; // <-- FIX: enable zIndex sorting
    this.container.addChild(this.modalContainer);

    this.overlay = new PIXI.Graphics();
    this.overlay.eventMode = 'static';
    this.overlay.cursor = 'default';
    this.overlay.zIndex = 0;
    this.overlay.on('pointerdown', (event: PIXI.FederatedPointerEvent) => {
      event.stopPropagation();
      this.hide();
    });
    this.modalContainer.addChild(this.overlay);

    this.panel = new PIXI.Graphics();
    this.panel.eventMode = 'static';
    this.panel.zIndex = 1;
    this.modalContainer.addChild(this.panel);

    this.tutorialTitle = new PIXI.Text('Tutorial', {
      fontFamily: 'Rosarivo, serif',
      fontSize: 30,
      fill: 0xf6e7b2,
      fontWeight: 'bold',
      padding: 6,
    });
    this.tutorialTitle.resolution = window.devicePixelRatio || 2;
    this.tutorialTitle.anchor.set(0, 0);
    this.tutorialTitle.zIndex = 2;
    this.modalContainer.addChild(this.tutorialTitle);

    this.tutorialDescription = new PIXI.Text(
      'Selamat datang di Desa Bacoputi.\nGunakan panduan dibawah untuk berjelajah, berinteraksi dan menyelesaikan misi.',
      {
        fontFamily: 'Rosarivo, serif',
        fontSize: 19,
        fill: 0xf7f0dd,
        lineHeight: 36,
        padding: 4,
      },
    );
    this.tutorialDescription.resolution = window.devicePixelRatio || 2;
    this.tutorialDescription.anchor.set(0, 0);
    this.tutorialDescription.zIndex = 2;
    this.modalContainer.addChild(this.tutorialDescription);

    this.closeButton = this.createCloseButton();
    this.closeButton.zIndex = 10; // <-- FIX: high zIndex so it's always on top
    this.modalContainer.addChild(this.closeButton);

    this.mobileTabButton = this.createTabButton('Mobile', 'mobile');
    this.mobileTabButton.zIndex = 2;
    this.pcTabButton = this.createTabButton('PC', 'pc');
    this.pcTabButton.zIndex = 2;
    this.tabButtons = {
      mobile: this.mobileTabButton,
      pc: this.pcTabButton,
    };
    this.modalContainer.addChild(this.mobileTabButton);
    this.modalContainer.addChild(this.pcTabButton);

    this.mobileTabButton.visible = false;
    this.pcTabButton.visible = false;

    this.tutorialImage = new PIXI.Sprite();
    this.tutorialImage.anchor.set(0.5);
    this.tutorialImage.zIndex = 2;
    this.modalContainer.addChild(this.tutorialImage);

    this.loadAssets().then(() => {
      this.draw();
    });

    this.draw();
    this.setActiveTab(this.activeTab);
  }

  private async loadAssets(): Promise<void> {
    try {
      const [buttonTexture, mobileTexture, pcTexture] = await Promise.all([
        PIXI.Assets.load('/assets/ui/b_question.png'),
        PIXI.Assets.load('/assets/ui/Tutorial-Mobile.png'),
        PIXI.Assets.load('/assets/ui/Tutorial-PC.png'),
      ]);
      this.buttonSprite.texture = buttonTexture;
      this.buttonSprite.tint = 0xffffff;
      this.tutorialTextures.mobile = mobileTexture;
      this.tutorialTextures.pc = pcTexture;
      this.setActiveTab(this.activeTab);
      this.draw();
    } catch (error) {
      console.error('Failed to load tutorial assets', error);
    }
  }

  private createCloseButton(): PIXI.Container {
    const container = new PIXI.Container();
    container.eventMode = 'static';
    container.cursor = 'pointer';
    container.sortableChildren = true; // <-- FIX: so bg and icon zIndex works

    const bg = new PIXI.Graphics();
    bg.name = 'background';
    bg.zIndex = 0;
    container.addChild(bg);

    const icon = new PIXI.Graphics();
    icon.name = 'icon';
    icon.zIndex = 1; // <-- FIX: icon always above background
    container.addChild(icon);

    container.on('pointerdown', (event: PIXI.FederatedPointerEvent) => {
      event.stopPropagation();
      this.hide();
    });

    return container;
  }

  private drawCloseButton(size: number): void {
    const bg = this.closeButton.getChildByName('background') as PIXI.Graphics;
    const icon = this.closeButton.getChildByName('icon') as PIXI.Graphics;
    const strokeWidth = size <= 34 ? 2.4 : 2.8;
    const inset = size * 0.28;

    bg.clear();
    bg.beginFill(0x5a2118, 0.98);
    bg.lineStyle(strokeWidth, 0xd4af37, 1);
    bg.drawRoundedRect(0, 0, size, size, Math.max(10, size * 0.24));
    bg.endFill();

    // FIX: draw icon using filled rectangles instead of lines to ensure it renders on all WebGL contexts
    icon.clear();
    icon.position.set(size / 2, size / 2);
    icon.rotation = Math.PI / 4;
    icon.beginFill(0xffffff);
    
    const length = (size - inset * 2) * 1.414;
    const thickness = Math.max(2, Math.round(strokeWidth + 1));
    
    icon.drawRoundedRect(-length / 2, -thickness / 2, length, thickness, thickness / 2);
    icon.drawRoundedRect(-thickness / 2, -length / 2, thickness, length, thickness / 2);
    icon.endFill();
  }

  private createTabButton(label: string, tab: TutorialTab): PIXI.Container {
    const container = new PIXI.Container();
    container.eventMode = 'static';
    container.cursor = 'pointer';

    const bg = new PIXI.Graphics();
    bg.name = 'background';
    container.addChild(bg);

    const text = new PIXI.Text(label, {
      fontFamily: 'Rosarivo, serif',
      fontSize: 20,
      fill: 0xfaf6eb,
      fontWeight: 'bold',
      padding: 4,
    });
    text.resolution = window.devicePixelRatio || 2;
    text.anchor.set(0.5);
    text.position.set(75, 22);
    text.name = 'label';
    container.addChild(text);

    container.on('pointerdown', (event: PIXI.FederatedPointerEvent) => {
      event.stopPropagation();
      this.setActiveTab(tab);
    });

    return container;
  }

  private setActiveTab(tab: TutorialTab): void {
    this.activeTab = tab;
    this.tutorialImage.texture = this.tutorialTextures[tab] ?? PIXI.Texture.EMPTY;

    (Object.keys(this.tabButtons) as TutorialTab[]).forEach(key => {
      const button = this.tabButtons[key];
      const bg = button.getChildByName('background') as PIXI.Graphics;
      const label = button.getChildByName('label') as PIXI.Text;
      const isActive = key === tab;

      bg.clear();
      bg.beginFill(isActive ? 0x8b6914 : 0x3d2818, 1);
      bg.lineStyle(2, isActive ? 0xf0d06d : 0x6e4a27, 1);
      bg.drawRoundedRect(0, 0, 150, 44, 14);
      bg.endFill();

      label.style.fill = isActive ? 0x1f1208 : 0xfaf6eb;
    });

    this.draw();
  }

  private draw(): void {
    const safeWidth = this.screenWidth || window.innerWidth || 1280;
    const safeHeight = this.screenHeight || window.innerHeight || 720;
    const isCompactLayout = safeWidth <= 768 || safeHeight <= 500;
    const panelHorizontalMargin = isCompactLayout ? 48 : 100;
    const panelVerticalMargin = isCompactLayout ? 44 : 60;
    const minPanelWidth = isCompactLayout ? 280 : 320;
    const minPanelHeight = isCompactLayout ? 200 : 360;
    const panelWidth = Math.min(
      isCompactLayout ? 620 : 840,
      Math.max(minPanelWidth, safeWidth - panelHorizontalMargin),
    );
    const panelHeight = Math.min(
      isCompactLayout ? 300 : 540,
      Math.max(minPanelHeight, safeHeight - panelVerticalMargin),
    );
    const panelX = (safeWidth - panelWidth) / 2;
    const panelY = (safeHeight - panelHeight) / 2;
    const titleFontSize = isCompactLayout ? 18 : 30;
    const titleOffsetX = isCompactLayout ? 18 : 32;
    const titleOffsetY = isCompactLayout ? 14 : 20;
    const descriptionOffsetX = titleOffsetX;
    const descriptionOffsetY = titleOffsetY + (isCompactLayout ? 28 : 54);
    const descriptionMaxWidth = panelWidth - titleOffsetX * 2 - (isCompactLayout ? 52 : 20);
    const descriptionFontSize = isCompactLayout ? 12 : 18;
    const descriptionLineHeight = isCompactLayout ? 16 : 28;
    const closeButtonSize = isCompactLayout ? 30 : 46;

    this.overlay.clear();
    this.overlay.beginFill(0x000000, 0.68);
    this.overlay.drawRect(0, 0, safeWidth, safeHeight);
    this.overlay.endFill();

    this.panel.clear();
    this.panel.beginFill(0x1f140c, 0.98);
    this.panel.lineStyle(3, 0xd4af37, 1);
    this.panel.drawRoundedRect(panelX, panelY, panelWidth, panelHeight, 24);
    this.panel.endFill();

    this.tutorialTitle.style.fontSize = titleFontSize;
    this.tutorialTitle.position.set(panelX + titleOffsetX, panelY + titleOffsetY);

    this.tutorialDescription.style.fontSize = descriptionFontSize;
    this.tutorialDescription.style.lineHeight = descriptionLineHeight;
    this.tutorialDescription.style.wordWrap = true;
    this.tutorialDescription.style.wordWrapWidth = descriptionMaxWidth;
    this.tutorialDescription.position.set(panelX + descriptionOffsetX, panelY + descriptionOffsetY);

    this.drawCloseButton(closeButtonSize);
    this.closeButton.position.set(panelX + panelWidth - closeButtonSize - 14, panelY + 10);
    this.mobileTabButton.position.set(panelX + 28, panelY + 88);
    this.pcTabButton.position.set(panelX + 188, panelY + 88);

    const imageTexture = this.tutorialImage.texture;
    const isMobileTutorial = this.activeTab === 'mobile';
    const contentTop = this.tutorialDescription.y + this.tutorialDescription.height + (isCompactLayout ? 12 : 20);
    const contentBottomPadding = isMobileTutorial ? (isCompactLayout ? 14 : 20) : isCompactLayout ? 12 : 18;
    const contentWidth = Math.max(
      180,
      panelWidth - (isMobileTutorial ? (isCompactLayout ? 168 : 170) : isCompactLayout ? 96 : 100),
    );
    const contentHeight = Math.max(120, panelY + panelHeight - contentTop - contentBottomPadding);

    if (imageTexture !== PIXI.Texture.EMPTY && imageTexture.width > 0 && imageTexture.height > 0) {
      let scale = Math.min(contentWidth / imageTexture.width, contentHeight / imageTexture.height);
      if (isMobileTutorial) {
        scale *= isCompactLayout ? 1.2 : 1.1;
      }
      this.tutorialImage.scale.set(scale);
    } else {
      this.tutorialImage.scale.set(1);
    }

    this.tutorialImage.position.set(panelX + panelWidth / 2, contentTop + contentHeight / 2);
  }

  public getContainer(): PIXI.Container {
    return this.container;
  }

  public getButtonHeight(): number {
    return this.buttonSize;
  }

  public getButtonGap(): number {
    return this.buttonGap;
  }

  public isVisible(): boolean {
    return this.isOpen;
  }

  public toggle(): void {
    if (this.isOpen) {
      this.hide();
      return;
    }
    this.show();
  }

  public show(): void {
    this.isOpen = true;
    this.buttonContainer.visible = false;
    this.modalContainer.visible = true;
  }

  public hide(): void {
    this.isOpen = false;
    this.modalContainer.visible = false;
    this.buttonContainer.visible = true;
  }

  public updateScreenSize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;

    const referenceWidth = 1280;
    const minScale = 0.7;
    const maxScale = 1.0;
    let nextScale = width / referenceWidth;
    nextScale = Math.max(minScale, Math.min(maxScale, nextScale));
    this.scale = nextScale;
    this.buttonContainer.scale.set(this.scale);

    const volumeWidth = 300;
    const volumeHeight = 80;
    const volumeButtonOffsetX = 68;
    const rightMargin = 10;
    const topMargin = 10;
    const volumeX = width - volumeWidth * this.scale - rightMargin;
    const tutorialScreenX = volumeX + (volumeWidth - volumeButtonOffsetX) * this.scale;
    const tutorialScreenY = topMargin + volumeHeight * this.scale + this.buttonGap;

    this.buttonContainer.position.set(tutorialScreenX, tutorialScreenY);

    this.draw();
  }

  public showButton(): void {
    this.container.visible = true;
  }

  public hideButton(): void {
    this.container.visible = false;
  }
}
